'use client';

import Link from 'next/link';
import Image from '@/components/media/StorefrontImage';
import type { AutocompleteResult } from '@/validators/search.validator';
import { isSpecialPriceActive } from '@/lib/special-price';
interface SearchDropdownProps {
  results: AutocompleteResult;
  query: string;
  onClose: () => void;
  activeIndex: number;
}
function formatPrice(price: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(Number.parseFloat(price));
}
export function SearchDropdown({
  results,
  query,
  onClose,
  activeIndex
}: SearchDropdownProps) {
  const hasPages = results.pages.length > 0;
  const hasCategories = results.categories.length > 0;
  const hasProducts = results.products.length > 0;
  const hasAny = hasPages || hasCategories || hasProducts;
  if (!hasAny) {
    return <div className="absolute top-full right-0 mt-1 w-[420px] max-w-[calc(100vw-2rem)] bg-white border border-gray-200 rounded-lg shadow-xl z-50 p-4">
        <p className="text-sm text-gray-500">No results for &ldquo;{query}&rdquo;</p>
      </div>;
  }
  let flatIndex = 0;
  return <div className="absolute top-full right-0 mt-1 w-[420px] max-w-[calc(100vw-2rem)] bg-white border border-gray-200 rounded-lg shadow-xl z-50 max-h-[70vh] overflow-y-auto">
      {/* Pages */}
      {hasPages && <div className="border-b border-gray-100 p-3">
          <p className="text-[11px] font-bold uppercase tracking-wider text-gray-400 mb-2">Pages</p>
          {results.pages.map(page => {
        const idx = flatIndex++;
        return <Link key={page.url} href={page.url} onClick={onClose} className={`block px-2 py-1.5 text-sm rounded transition-colors ${idx === activeIndex ? 'bg-gray-100 text-[#1B5078]' : 'text-gray-700 hover:bg-gray-50'}`}>
                {page.title}
              </Link>;
      })}
        </div>}

      {/* Categories */}
      {hasCategories && <div className="border-b border-gray-100 p-3">
          <p className="text-[11px] font-bold uppercase tracking-wider text-gray-400 mb-2">Category</p>
          {results.categories.map(cat => {
        const idx_0 = flatIndex++;
        return <Link key={cat.slug} href={cat.url} onClick={onClose} className={`block px-2 py-1.5 text-sm rounded transition-colors ${idx_0 === activeIndex ? 'bg-gray-100 text-[#1B5078]' : 'text-gray-700 hover:bg-gray-50'}`}>
                {cat.name}
              </Link>;
      })}
        </div>}

      {/* Products */}
      {hasProducts && <div className="p-3">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[11px] font-bold uppercase tracking-wider text-gray-400">Products</p>
            <Link href={`/search?q=${encodeURIComponent(query)}`} onClick={onClose} className="text-xs font-semibold text-[#ED8326] hover:underline">
              See All
            </Link>
          </div>
          {results.products.map(product => {
        const idx_1 = flatIndex++;
        const hasSpecial = product.specialPrice && isSpecialPriceActive(product.specialFrom, product.specialTo);
        return <Link key={product.sku} href={product.url} onClick={onClose} className={`flex items-center gap-3 px-2 py-2 rounded transition-colors ${idx_1 === activeIndex ? 'bg-gray-100' : 'hover:bg-gray-50'}`}>
                {product.imageUrl ? <Image src={product.imageUrl} alt={product.name} width={48} height={48} className="rounded border border-gray-200 object-contain" /> : <div className="w-12 h-12 rounded border border-gray-200 bg-gray-50 flex items-center justify-center">
                    <span className="text-[10px] text-gray-400">No img</span>
                  </div>}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[#1B5078] truncate">{product.name}</p>
                  <div className="flex items-baseline gap-1.5">
                    {hasSpecial ? <>
                        <span className="text-sm font-semibold text-green-700">
                          {formatPrice(product.specialPrice!)}
                        </span>
                        <span className="text-xs text-gray-400 line-through">
                          {formatPrice(product.basePrice)}
                        </span>
                      </> : <span className="text-sm font-semibold text-green-700">
                        {formatPrice(product.basePrice)}
                      </span>}
                  </div>
                </div>
              </Link>;
      })}
        </div>}
    </div>;
}